import React from "react";

/* ------------------------------------------------------------------ */
/*  Notebook Doodles                                                   */
/*  Tiny hand-drawn sketches, like a rider's field notebook.           */
/*  All accepts `size` (px) and optional `color`. Meant to be small    */
/*  (32–120px) and used as marginalia/Easter eggs, never hero art.     */
/* ------------------------------------------------------------------ */

const INK = "#F4EEE0";
const ACID = "#C9FF3D";

/* Tiny arrow scribble — hand-drawn "look here" pointer */
export function ScribbleArrow({ size = 60, color = ACID, rotate = -18, className = "" }) {
    return (
        <svg viewBox="0 0 80 60" width={size} className={className} style={{ transform: `rotate(${rotate}deg)` }} aria-hidden="true">
            <path d="M4 40 C 18 30, 30 20, 50 16 M 42 8 L 54 16 L 46 26" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

/* Compass rose — tiny wireframe compass */
export function CompassRose({ size = 64, color = ACID, className = "", rotate = 0 }) {
    return (
        <svg viewBox="0 0 80 80" width={size} className={className} style={{ transform: `rotate(${rotate}deg)` }} aria-hidden="true">
            <circle cx="40" cy="40" r="30" fill="none" stroke={color} strokeWidth="1" />
            <circle cx="40" cy="40" r="22" fill="none" stroke={color} strokeWidth="0.6" strokeDasharray="1 2" />
            <path d="M40 12 L 44 40 L 40 68 L 36 40 Z" fill={color} opacity="0.85" />
            <path d="M12 40 L 40 44 L 68 40 L 40 36 Z" fill="none" stroke={color} strokeWidth="0.8" />
            <text x="40" y="10" fontFamily="Space Mono, monospace" fontSize="8" fill={color} textAnchor="middle">N</text>
        </svg>
    );
}

/* Chainring — tiny bike gear sketch */
export function ChainringDoodle({ size = 72, color = ACID, className = "", rotate = 0 }) {
    const teeth = 12;
    return (
        <svg viewBox="0 0 80 80" width={size} className={className} style={{ transform: `rotate(${rotate}deg)` }} aria-hidden="true">
            {[...Array(teeth)].map((_, i) => {
                const a = (i / teeth) * Math.PI * 2;
                const x = 40 + Math.cos(a) * 32;
                const y = 40 + Math.sin(a) * 32;
                return <circle key={i} cx={x} cy={y} r="1.6" fill={color} />;
            })}
            <circle cx="40" cy="40" r="26" fill="none" stroke={color} strokeWidth="1.2" />
            <circle cx="40" cy="40" r="8" fill="none" stroke={color} strokeWidth="1.2" />
            {/* Cross spokes */}
            <path d="M40 14 L 40 66 M 14 40 L 66 40 M 22 22 L 58 58 M 58 22 L 22 58" stroke={color} strokeWidth="0.8" />
        </svg>
    );
}

/* Tire tread stripe — small tread pattern */
export function TireTread({ width = 120, height = 18, color = ACID, className = "", rotate = 0 }) {
    return (
        <svg viewBox="0 0 120 18" width={width} height={height} className={className} style={{ transform: `rotate(${rotate}deg)` }} aria-hidden="true">
            {[...Array(10)].map((_, i) => (
                <g key={i} transform={`translate(${i * 12} 0)`}>
                    <path d="M2 2 L 8 2 L 10 9 L 6 16 L 0 9 Z" fill="none" stroke={color} strokeWidth="1" />
                </g>
            ))}
        </svg>
    );
}

/* Topography contour — hand-drawn elevation lines (purely decorative — no numbers) */
export function ContourSketch({ width = 140, height = 60, color = ACID, className = "" }) {
    return (
        <svg viewBox="0 0 140 60" width={width} height={height} className={className} aria-hidden="true">
            <path d="M4 50 C 30 44, 50 40, 78 42 C 100 44, 120 48, 136 46" fill="none" stroke={color} strokeWidth="1" />
            <path d="M8 40 C 34 32, 58 26, 82 28 C 104 30, 118 34, 132 34" fill="none" stroke={color} strokeWidth="1" />
            <path d="M14 30 C 40 22, 62 16, 84 18 C 100 20, 116 22, 128 24" fill="none" stroke={color} strokeWidth="1" />
            <path d="M22 22 C 44 14, 66 10, 82 12 C 96 14, 108 16, 118 18" fill="none" stroke={color} strokeWidth="1" opacity="0.7" />
            <circle cx="82" cy="14" r="2" fill={color} />
        </svg>
    );
}

/* Altitude marker — tiny mountain peak triangle */
export function PeakMark({ size = 40, color = ACID, className = "", label = "" }) {
    return (
        <svg viewBox="0 0 40 40" width={size} className={className} aria-hidden="true">
            <path d="M4 32 L 18 8 L 26 22 L 32 14 L 36 32 Z" fill="none" stroke={color} strokeWidth="1.4" />
            <circle cx="18" cy="8" r="1.8" fill={color} />
            {label && <text x="20" y="38" fontFamily="Space Mono, monospace" fontSize="7" fill={color} textAnchor="middle">{label}</text>}
        </svg>
    );
}

/* Hand-written note — rotated sticky annotation */
export function HandNote({ children, rotate = -3, color = ACID, className = "", underline = true }) {
    return (
        <span
            className={`inline-block font-vt lowercase ${className}`}
            style={{
                transform: `rotate(${rotate}deg)`,
                color,
                fontSize: "0.95rem",
                letterSpacing: "0.02em",
                textDecoration: underline ? "underline wavy" : "none",
                textUnderlineOffset: "3px",
                textDecorationThickness: "1px",
            }}
        >
            {children}
        </span>
    );
}

/* "Stay Abnormal" stamp — small round rubber stamp */
export function StayAbnormalStamp({ size = 90, color = ACID, className = "", rotate = -8 }) {
    return (
        <div
            className={`relative inline-block pointer-events-none select-none ${className}`}
            style={{ width: size, height: size, transform: `rotate(${rotate}deg)` }}
            aria-hidden="true"
        >
            <svg viewBox="0 0 120 120" className="w-full h-full">
                <circle cx="60" cy="60" r="54" fill="none" stroke={color} strokeWidth="1.4" />
                <circle cx="60" cy="60" r="48" fill="none" stroke={color} strokeWidth="0.6" strokeDasharray="1.5 2" />
                <defs>
                    <path id="stay-arc" d="M 20 60 A 40 40 0 1 1 20 61" />
                </defs>
                <text fill={color} fontFamily="Space Mono, monospace" fontSize="8" letterSpacing="2.2">
                    <textPath href="#stay-arc" startOffset="0%">STAY ABNORMAL · STAY WEIRD · STAY ABNORMAL · </textPath>
                </text>
                <text x="60" y="58" textAnchor="middle" fontFamily="Space Mono, monospace" fontSize="13" fontWeight="bold" fill={color}>STAY</text>
                <text x="60" y="72" textAnchor="middle" fontFamily="Space Mono, monospace" fontSize="13" fontWeight="bold" fill={color}>WEIRD</text>
            </svg>
        </div>
    );
}

/* Barcode strip — retro '90s sticker vibe */
export function AbnormalBarcode({ width = 140, height = 40, color = ACID, className = "" }) {
    const bars = [3, 1, 2, 1, 3, 1, 1, 2, 1, 3, 1, 2, 1, 1, 3, 2, 1, 1, 2, 1, 3, 1];
    let x = 0;
    return (
        <svg viewBox={`0 0 ${bars.reduce((a, b) => a + b + 1, 0)} 40`} width={width} height={height} className={className} aria-hidden="true">
            {bars.map((w, i) => {
                const el = <rect key={i} x={x} y="0" width={w} height="28" fill={color} />;
                x += w + 1;
                return el;
            })}
            <text x="0" y="38" fontFamily="Space Mono, monospace" fontSize="7" fill={color}>ABNRML · 26 · MMXXVI</text>
        </svg>
    );
}

/* Checkbox scribble — hand-drawn checkbox with acid check */
export function CheckDoodle({ size = 22, color = ACID, className = "", checked = true }) {
    return (
        <svg viewBox="0 0 24 24" width={size} className={className} aria-hidden="true">
            <path d="M2 4 L 20 3 L 21 21 L 3 22 Z" fill="none" stroke={color} strokeWidth="1.2" />
            {checked && <path d="M6 12 L 11 17 L 18 8" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
        </svg>
    );
}
